import React, { useMemo } from 'react';
import { MapPin, Navigation } from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import { themeClasses } from '../../config/themeClasses';
import { Card } from '../UI/card';
import { Badge } from '../UI/badge';
import SupplyChainMap from '../Maps/SupplyChainMap';
import LandingShowcaseSection from './LandingShowcaseSection';
import LandingMapLegend from './LandingMapLegend';
import {
  buildLandingMapMarkers,
  buildLandingMapRoutes,
  LANDING_ROUTE_DEMO,
  getRiskSoftClass,
} from './landingDemoData';

function LandingRoutePreviewCard() {
  const { t } = useLocale();
  const { riskScore, isActive } = LANDING_ROUTE_DEMO;
  const riskClass = getRiskSoftClass(riskScore);

  return (
    <Card className={`${themeClasses.landing.showcaseCard} h-full min-h-0`}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">{t('landingDemoRouteName')}</h3>
        <Badge
          variant="outline"
          className={
            isActive
              ? 'border-success-500/30 bg-success-500/10 text-success-500'
              : 'border-border-light bg-canvas-soft text-text-secondary'
          }
        >
          {isActive ? t('active') : t('routeDetailsInactive')}
        </Badge>
      </div>

      <div
        className={`flex-1 overflow-hidden rounded-xl border-2 ${
          isActive ? 'border-success-500/30 bg-success-500/5' : 'border-border-light bg-canvas-soft'
        }`}
      >
        <div
          className={`flex items-start gap-3 p-4 ${
            isActive ? 'bg-success-500/10' : 'bg-canvas-soft-2'
          }`}
        >
          <div
            className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${
              isActive ? 'bg-success-500' : 'bg-background-tertiary'
            }`}
          >
            <Navigation className="size-5 text-text-inverse" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-text-primary">
              {t('landingDemoRouteName')}
            </p>
            <div className="mt-2 flex flex-wrap gap-2 text-xs text-text-secondary">
              <span>{t('landingDemoRouteDistance')}</span>
              <span className="text-text-tertiary">•</span>
              <span>{t('landingDemoRouteDuration')}</span>
            </div>
          </div>
          <Badge variant="outline" className={riskClass}>
            {riskScore}/100
          </Badge>
        </div>

        <div className="space-y-3 p-4">
          <div className="flex items-start gap-3">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-success-500/20">
              <MapPin className="size-4 text-success-500" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-text-secondary">{t('routeDetailsStartingPoint')}</p>
              <p className="mt-0.5 truncate text-sm font-semibold text-text-primary">
                {t('landingDemoRouteStart')}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-center">
            <div className="h-0.5 w-full bg-border-light" />
          </div>
          <div className="flex items-start gap-3">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-danger-500/20">
              <MapPin className="size-4 text-danger-500" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-text-secondary">{t('routeDetailsDestination')}</p>
              <p className="mt-0.5 truncate text-sm font-semibold text-text-primary">
                {t('landingDemoRouteEnd')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <p className="mt-4 text-xs leading-relaxed text-text-tertiary">{t('landingMapRoutesRouteHint')}</p>
    </Card>
  );
}

export default function LandingMapRoutesShowcase() {
  const { t } = useLocale();
  const markers = useMemo(() => buildLandingMapMarkers(t), [t]);
  const routes = useMemo(() => buildLandingMapRoutes(), []);

  return (
    <LandingShowcaseSection
      eyebrowKey="landingMapRoutesEyebrow"
      titleKey="landingMapRoutesTitle"
      subtitleKey="landingMapRoutesSubtitle"
    >
      <div className={themeClasses.landing.showcaseGrid}>
        <div className={`${themeClasses.landing.showcaseMapCol} h-[420px] lg:h-[400px]`}>
          <Card className={`${themeClasses.landing.showcaseCard} h-full min-h-0`}>
            <h3 className="mb-4 shrink-0 text-lg font-semibold tracking-tight text-text-primary">
              {t('landingMapRoutesMapTitle')}
            </h3>
            <div className="relative min-h-0 flex-1 overflow-hidden rounded-lg border border-border-light">
              <SupplyChainMap
                markers={markers}
                routes={routes}
                compact
                minHeight={280}
                className="h-full"
              />
            </div>
            <LandingMapLegend />
          </Card>
        </div>
        <div className={`${themeClasses.landing.showcaseSideCol} h-[420px] lg:h-[400px]`}>
          <LandingRoutePreviewCard />
        </div>
      </div>
    </LandingShowcaseSection>
  );
}
